import { baseApi } from './api';
import {
    Ticket,
    TicketDirectory,
    TicketPageResultType,
    TicketResultType,
} from 'types/User/firebaseTypes/ticket';

export const planningApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        /** Get a page of delivery tickets for the supplier. */
        getDeliveryTicketsPage: builder.query<
            TicketPageResultType,
            {
                supplierId: string | null;
                ticketDirectory: TicketDirectory;
                startAt?: string;
                limit?: number;
            }
        >({
            query: ({ supplierId, startAt, limit, ticketDirectory }) => {
                if (supplierId?.toString() === '0') {
                    throw new Error(
                        'Should not have delivery tickets under supplierId 0.'
                    );
                }

                if (!supplierId) throw new Error('supplierId is required.');

                if (!ticketDirectory) {
                    throw new Error('ticketDirectory is required.');
                }

                return {
                    method: 'GET',
                    url: `tickets/${supplierId}/delivery/${ticketDirectory}`,
                    params: {
                        paginate: true,
                        startAt,
                        limit,
                    },
                };
            },
        }),
        getDeliveryTicketsByDeviceId: builder.query<
            Ticket,
            { supplierId: string | null; deviceId: string | null }
        >({
            query: ({ supplierId, deviceId }) => ({
                method: 'GET',
                url: `tickets/${supplierId}/delivery/active/${deviceId}`,
            }),
            transformResponse: (res: any) => {
                return { ...res };
            },
            transformErrorResponse: (res: any) => {
                return { ...res };
            },
        }),
        /** Get resolved delivery tickets for a specific device. */
        getResolvedDeliveryTicketsByDeviceId: builder.query<
            TicketResultType,
            { supplierId: string | null; deviceId: string | null }
        >({
            query: ({ supplierId, deviceId }) => ({
                method: 'GET',
                url: `tickets/${supplierId}/delivery/resolved/${deviceId}`,
            }),
            transformResponse: (res: any) => {
                return { ...res };
            },
            transformErrorResponse: (res: any) => {
                return { ...res };
            },
        }),
        createDeliveryTicket: builder.query<
            Ticket,
            {
                supplierId: string | null;
                deviceId: string;
                ticket: Partial<Ticket>;
            }
        >({
            query: ({ supplierId, deviceId, ticket }) => ({
                method: 'POST',
                url: `tickets/${supplierId}/delivery/active/${deviceId}`,
                body: ticket,
            }),
        }),
        updateDeliveryTicket: builder.query<
            Ticket,
            {
                supplierId: string;
                deviceId: string;
                newTicket: Ticket;
            }
        >({
            query: ({ newTicket, supplierId, deviceId }) => ({
                method: 'PUT',
                url: `tickets/${supplierId}/delivery/active/${deviceId}`,
                body: newTicket,
            }),
            transformResponse: (response: any) => {
                return { ...response };
            },
        }),
        /** NOTE: This doesn't seem currently supported by napi. */
        updateResolvedDeliveryTicket: builder.query<
            Ticket,
            {
                supplierId: string;
                deviceId: string;
                newTicket: Ticket;
            }
        >({
            query: ({ newTicket, supplierId, deviceId }) => ({
                method: 'PUT',
                url: `tickets/${supplierId}/delivery/resolved/${deviceId}`,
                body: newTicket,
            }),
            transformResponse: (response: any) => {
                return { ...response };
            },
        }),
        /** Move an active delivery ticket to resolved status. */
        resolveDeliveryTicket: builder.query<
            Ticket,
            { supplierId: string; deviceId: string; newTicket: Ticket }
        >({
            query: ({ newTicket, supplierId, deviceId }) => ({
                method: 'POST',
                url: `tickets/${supplierId}/delivery/resolved/${deviceId}`,
                body: newTicket,
            }),
            transformResponse: (response: any) => {
                return { ...response };
            },
        }),
        createPreResolvedTicket: builder.query<
            Ticket,
            { supplierId: string; deviceId: string; ticket: Partial<Ticket> }
        >({
            query: ({ ticket, supplierId, deviceId }) => ({
                method: 'POST',
                url: `tickets/${supplierId}/delivery/resolved/${deviceId}/pre-resolved`,
                body: ticket,
            }),
        }),
    }),
});

export const {
    useLazyGetDeliveryTicketsPageQuery,
    useLazyGetDeliveryTicketsByDeviceIdQuery,
    useLazyGetResolvedDeliveryTicketsByDeviceIdQuery,
    useLazyUpdateDeliveryTicketQuery,
    useLazyCreateDeliveryTicketQuery,
    useLazyUpdateResolvedDeliveryTicketQuery,
    useLazyResolveDeliveryTicketQuery,
    useLazyCreatePreResolvedTicketQuery,
} = planningApi;
